import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * The design-system font-size scale (`--text-ds-*` in styles/theme.css). tailwind-merge cannot
 * tell `text-ds-base` (a size) from `text-ds-on-primary` (a colour), so without this group a
 * size silently erased the text colour (TO-370: a primary Badge rendered body text on primary).
 */
export const DS_FONT_SIZES = ['ds-xs', 'ds-sm', 'ds-base', 'ds-lg', 'ds-xl', 'ds-2xl', 'ds-3xl', 'ds-4xl', 'ds-5xl']

/** The design-system semantic type roles (`--text-ds-<role>` in styles/theme.css, TO-650). */
export const DS_TYPE_ROLES = ['ds-heading-1', 'ds-heading-2', 'ds-heading-3', 'ds-body', 'ds-caption']

/** The design-system font weights (`--font-weight-ds-*` in styles/theme.css, TO-650). */
export const DS_FONT_WEIGHTS = ['ds-light', 'ds-regular', 'ds-medium', 'ds-semibold', 'ds-bold']

/** The design-system line heights (`--leading-ds-*` in styles/theme.css, TO-650). */
export const DS_LINE_HEIGHTS = ['ds-tight', 'ds-normal', 'ds-relaxed']

/** The design-system motion durations (`--transition-duration-ds-*` in styles/theme.css). */
export const DS_DURATIONS = ['ds-fast', 'ds-base', 'ds-slow', 'ds-slower']

/** The design-system motion easings (`--ease-ds-*` in styles/theme.css). */
export const DS_EASINGS = ['ds-standard', 'ds-in', 'ds-out', 'ds-in-out']

const twMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			'font-size': [{ text: [...DS_FONT_SIZES, ...DS_TYPE_ROLES] }],
			// TO-650: tailwind-merge reads an unknown `font-<x>` as a font family, so without this a
			// weight override (`font-ds-semibold`) would erase the brand family (`font-ds-body`).
			'font-weight': [{ font: DS_FONT_WEIGHTS }],
			leading: [{ leading: DS_LINE_HEIGHTS }],
			// TO-643: tailwind-merge only knows numeric durations and its own easings, so a ds motion
			// override would otherwise sit next to the class it was meant to replace.
			duration: [{ duration: DS_DURATIONS }],
			ease: [{ ease: DS_EASINGS }],
		},
	},
})

/**
 * Merge class names, resolving conflicting Tailwind utilities so the last one wins.
 * The single class-composition helper shared by every `@today/ui-*` package.
 */
export function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs))
}
